// ============================================================
// GraficoDeEvolucao.js — a série do atleta desenhada
//
// Uma linha por indicador, com a data de cada coleta no eixo horizontal.
// Serve à avaliação física e à ficha do atleta, que mostram a mesma série
// e não devem desenhá-la de dois jeitos.
//
// ── Um gráfico por medida ───────────────────────────────────
//
// Desde 15/10/2026, a avaliação física desenha uma medida por gráfico,
// pequeno, com a unidade no título e o valor escrito em cada ponto.
//
// O que havia antes era duas linhas por gráfico com um eixo de cada
// lado, e o Diego apontou o problema: com escalas diferentes, a
// proximidade entre as duas linhas é produzida pela escala e não pelos
// dados. Duas curvas que se cruzam num gráfico de eixo duplo sugerem uma
// relação que pode não existir, e a magnitude de cada mudança fica
// ilegível. Com um eixo só por desenho, o que se vê é o que mudou.
//
// `GraficoDeMedida` é o formato pequeno, e desde 15/10/2026 é o único:
// a ficha do atleta passou a desenhar igual à avaliação física, e o
// componente de duas linhas ficou sem nenhuma tela. Ele saiu em vez de
// ficar esperando: código sem uso, com um comentário dizendo que alguém
// o usa, é o que faz a próxima pessoa decidir errado.
//
// Ele sabia uma coisa que este não sabe: juntar duas medidas da MESMA
// unidade num eixo só, massa adiposa e massa muscular em quilos. Se isso
// voltar a ser pedido, está no histórico, no commit que o removeu.
//
// ── O ponto que não existe ──────────────────────────────────
//
// Coleta sem o indicador não vira zero nem some do eixo: vira buraco na
// linha, com `connectNulls` desligado. Uma avaliação em que não mediram a
// dobra do bíceps não é uma avaliação com zero de gordura, e ligar os
// pontos por cima do buraco desenharia uma reta que atravessa um mês em
// que ninguém mediu nada.
// ============================================================

import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, LabelList,
  ResponsiveContainer,
} from "recharts";
import {
  AZUL, TEXTO, CINZA_ESC, BORDA, FUNDO_SUAVE, RAIO, TIPO,
  AZUL_ESCURO, AZUL_CLARO, ESP,
} from "../tema";
import { Vazio } from "./Dados";
import { rotuloDoProtocolo } from "../avaliacaoFisica";

function emBR(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ""))) return String(iso || "");
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a.slice(2)}`;
}

// A mesma data com o ano inteiro. O eixo do gráfico abrevia porque o rótulo
// compete por espaço com o vizinho; frase não tem esse problema, e "10/06/22"
// escrito no meio de um texto faz quem lê parar para conferir o ano.
function emBRCompleto(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ""))) return String(iso || "");
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

// ── A escala vertical de cada medida ─────────────────────────
//
// O eixo automático do recharts enquadra o que existe, e é isso que
// transforma diferença pequena em curva acentuada: 0,3 kg entre duas
// coletas vira uma subida que ocupa a altura inteira do desenho, e quem
// olha de longe lê salto onde houve ruído de balança.
//
// A regra aqui tem duas partes. A amplitude nunca é menor que a mínima
// da medida, e nunca é menor que o dobro do que o atleta variou: a
// variação ocupa no máximo metade da altura, sobrando margem acima e
// abaixo para a leitura não encostar nas bordas. Depois disso os limites
// são arredondados para fora, no passo da medida, para as marcações
// caírem em números redondos.
//
// ── Por que isso também estabiliza a comparação ─────────────
//
// Arredondar para fora faz a escala PARAR de mudar a cada coleta nova.
// Enquanto os valores ficarem dentro da mesma faixa redonda, trocar o
// período, acrescentar uma avaliação ou abrir outro atleta devolve o
// mesmo eixo, e dois gráficos lado a lado podem ser comparados a olho.
// Com o eixo automático, cada série ganhava a sua própria régua.
//
// ── O que estas faixas NÃO são ──────────────────────────────
//
// Não são faixas de referência clínica nem de desempenho. São escolha de
// apresentação: a amplitude mínima de cada medida foi posta no tamanho
// da variação que a equipe considera relevante ver, e pode ser ajustada
// por atleta ou por modalidade sem que nada no cálculo mude.
export function dominioDaMedida(valores, { amplitudeMinima = 1, passo = 1 } = {}) {
  const nums = valores.filter(Number.isFinite);
  if (!nums.length) return ["auto", "auto"];

  const menor = Math.min(...nums);
  const maior = Math.max(...nums);
  const centro = (menor + maior) / 2;
  const amplitude = Math.max(amplitudeMinima, (maior - menor) * 2);

  const de = Math.floor((centro - amplitude / 2) / passo) * passo;
  const ate = Math.ceil((centro + amplitude / 2) / passo) * passo;
  // Arredondar para fora pode deixar os dois limites iguais quando a
  // amplitude é menor que o passo. Uma faixa de altura zero não desenha.
  return [de, ate > de ? ate : de + passo];
}

// ── Um gráfico pequeno, de uma medida só ────────────────────
//
// O título diz o que é e em que unidade, o valor fica escrito em cada
// ponto e o eixo é um só. É o formato que a avaliação física usa, em
// grade: lado a lado eles comparam datas, e nenhum deles sugere relação
// entre medidas, que é o que o eixo duplo fazia.
//
// A variação total aparece ao lado do título, porque é a pergunta que se
// faz olhando um gráfico de uma medida só: mudou quanto, no fim das
// contas.
export function GraficoDeMedida({
  serie = [],
  indicador,
  altura = 150,
  vazio = null,
}) {
  const pontos = serie
    .filter(p => p && p.data)
    .map(p => {
      const v = indicador.valor(p);
      return { data: emBR(p.data), valor: Number.isFinite(v) ? v : null };
    });
  const medidos = pontos.filter(p => p.valor != null);

  const casas = indicador.casas ?? 1;
  const emPT = v => (v == null ? "—" : v.toFixed(casas).replace(".", ","));
  const titulo = `${indicador.rotulo}${indicador.unidade ? ` (${indicador.unidade})` : ""}`;

  if (medidos.length < 2) {
    return (
      <div>
        <Cabecalho titulo={titulo} variacao={null} />
        <Vazio titulo={vazio || `${indicador.rotulo} precisa de duas coletas medidas.`} />
      </div>
    );
  }

  const variacao = medidos[medidos.length - 1].valor - medidos[0].valor;

  return (
    <div>
      <Cabecalho titulo={titulo} variacao={variacao} emPT={emPT} />
      <div style={{ width: "100%", height: altura }}>
        <ResponsiveContainer>
          {/* A margem à esquerda era negativa, e cortava o número do
              eixo em medida de três dígitos com decimal, como a estatura
              e a massa muscular. */}
          <LineChart data={pontos} margin={{ top: 18, right: 18, left: 2, bottom: 2 }}>
            <CartesianGrid stroke={BORDA} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="data" tick={{ fontSize: 10.5, fill: CINZA_ESC }} stroke={BORDA} />
            <YAxis
              tick={{ fontSize: 10.5, fill: CINZA_ESC }}
              stroke={BORDA}
              width={48}
              // A faixa vem de `dominioDaMedida`, e não do automático do
              // recharts: ver a nota acima dela.
              domain={dominioDaMedida(medidos.map(p => p.valor), indicador)}
              tickFormatter={v => emPT(v)}
            />
            <Tooltip
              contentStyle={{
                background: FUNDO_SUAVE, border: `1px solid ${BORDA}`,
                borderRadius: RAIO.md, fontSize: 12.5, color: TEXTO,
              }}
              formatter={v => [`${emPT(v)}${indicador.unidade ? " " + indicador.unidade : ""}`,
                indicador.rotulo]}
            />
            <Line
              // Segmento reto entre as coletas, e não curva suavizada. A
              // curva desenha um caminho entre duas datas que ninguém
              // mediu, e sugere que o atleta passou por valores que
              // nunca foram medidos.
              type="linear"
              dataKey="valor"
              name={indicador.rotulo}
              stroke={AZUL}
              strokeWidth={2}
              dot={{ r: 3.5, fill: AZUL }}
              activeDot={{ r: 5 }}
              // Buraco continua buraco: ver nota do cabeçalho.
              connectNulls={false}
              isAnimationActive={false}
            >
              {/* O valor escrito no ponto. Num gráfico pequeno, ler a
                  altura contra o eixo custa mais do que ler o número. */}
              <LabelList
                dataKey="valor"
                position="top"
                offset={8}
                formatter={v => (v == null ? "" : emPT(v))}
                style={{ fontSize: 10.5, fill: TEXTO, fontWeight: 600 }}
              />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Cabecalho({ titulo, variacao, emPT }) {
  return (
    <div style={{
      display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8,
    }}>
      <span style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>{titulo}</span>
      {variacao != null && (
        <span style={{ ...TIPO.apoio, color: CINZA_ESC }}>
          {variacao > 0 ? "+" : variacao < 0 ? "−" : ""}
          {emPT(Math.abs(variacao))} no período
        </span>
      )}
    </div>
  );
}

// Os indicadores que a avaliação física desenha, prontos para usar.
//
// Ficam aqui e não na tela porque a ficha do atleta mostra os mesmos, e
// duas listas iguais em dois arquivos divergem na primeira vez que
// alguém acrescenta um indicador em um só.
export const INDICADORES = {
  gordura: {
    id: "gordura",
    rotulo: "Gordura",
    unidade: "%",
    casas: 1,
    // 8 pontos percentuais de faixa: meio ponto de diferença entre duas
    // coletas é erro de dobra, e não pode desenhar montanha.
    amplitudeMinima: 8,
    passo: 2,
    valor: p => (p.deurenberg && p.deurenberg.percentualGordura) ?? null,
  },
  massaMagra: {
    id: "massaMagra",
    rotulo: "Massa magra",
    unidade: "kg",
    casas: 1,
    amplitudeMinima: 8,
    passo: 2,
    valor: p => (p.deurenberg && p.deurenberg.massaMagra) ?? null,
  },
  // Massa adiposa e massa muscular saem do fracionamento, que precisa do
  // diâmetro do punho. A adiposa tem reserva no protocolo de dobras,
  // porque é o mesmo número: o fracionamento recebe o percentual pronto
  // em vez de calcular o dele.
  massaAdiposa: {
    id: "massaAdiposa",
    rotulo: "Massa adiposa",
    unidade: "kg",
    casas: 2,
    // A massa adiposa de uma criança anda entre 3 e 10 kg, então a
    // faixa dela é menor que a das outras massas.
    amplitudeMinima: 4,
    passo: 1,
    valor: p => (p.fracionamento && p.fracionamento.massaGorda)
      ?? (p.deurenberg && p.deurenberg.massaGorda) ?? null,
  },
  massaMuscular: {
    id: "massaMuscular",
    rotulo: "Massa muscular",
    unidade: "kg",
    casas: 2,
    amplitudeMinima: 8,
    passo: 2,
    valor: p => (p.fracionamento && p.fracionamento.massaMuscular) ?? null,
  },
  peso: {
    id: "peso", rotulo: "Peso", unidade: "kg", casas: 1,
    amplitudeMinima: 8, passo: 2,
    valor: p => p.peso ?? null,
  },
  // Uma casa decimal, e não zero: a estatura anda meio centímetro entre
  // coletas, e arredondar o EIXO para o inteiro imprimia "167" e "168"
  // repetidos, duas marcações com o mesmo rótulo.
  estatura: {
    id: "estatura", rotulo: "Estatura", unidade: "cm", casas: 1,
    // 10 cm de faixa: o estirão de um ano cabe inteiro no desenho, e
    // meio centímetro de erro de estadiômetro não vira degrau.
    amplitudeMinima: 10, passo: 2,
    valor: p => p.estatura ?? null,
  },
  imc: {
    id: "imc", rotulo: "IMC", unidade: "kg/m²", casas: 1,
    amplitudeMinima: 4, passo: 1,
    valor: p => (p.indices && p.indices.imc) ?? null,
  },
  endomorfia: {
    id: "endomorfia", rotulo: "Endomorfia", unidade: "", casas: 1,
    amplitudeMinima: 3, passo: 1,
    valor: p => (p.somatotipo && p.somatotipo.endomorfia) ?? null,
  },
  mesomorfia: {
    id: "mesomorfia", rotulo: "Mesomorfia", unidade: "", casas: 1,
    amplitudeMinima: 3, passo: 1,
    valor: p => (p.somatotipo && p.somatotipo.mesomorfia) ?? null,
  },
  ectomorfia: {
    id: "ectomorfia", rotulo: "Ectomorfia", unidade: "", casas: 1,
    amplitudeMinima: 3, passo: 1,
    valor: p => (p.somatotipo && p.somatotipo.ectomorfia) ?? null,
  },
};

// ── O aviso de troca de equação ─────────────────────────────
//
// O aviso de que a série de gordura mudou de equação no meio.
//
// Aparece só quando há mais de um protocolo na mesma série, que hoje
// acontece no atleta que fez 18 anos entre duas coletas. Não é erro de
// ninguém e não há o que corrigir: é informação que falta ao gráfico, e
// sem ela a comissão lê como evolução o que é, em parte, troca de régua.
//
// Mora aqui, e não na tela, porque a ficha do atleta desenha a MESMA série.
// Dito com palavras diferentes em cada tela, quem lê as duas não saberia se
// são o mesmo aviso ou dois problemas.
export function TrocaDeProtocolo({ serie }) {
  const usados = [];
  (serie || []).forEach((p) => {
    const prot = p.deurenberg && p.deurenberg.completo && p.deurenberg.protocolo;
    if (!prot) return;
    const ultimo = usados[usados.length - 1];
    if (!ultimo || ultimo.protocolo !== prot) usados.push({ protocolo: prot, data: p.data });
  });
  if (usados.length < 2) return null;

  const virada = usados[usados.length - 1];
  return (
    <div style={{
      ...TIPO.apoio, color: AZUL_ESCURO, background: AZUL_CLARO,
      border: "1px solid #BFD0EC", borderRadius: 10,
      padding: "10px 14px", marginTop: ESP.md, lineHeight: 1.6,
    }}>
      <b>A curva de gordura muda de equação no meio.</b>{" "}
      Até {emBRCompleto(usados[usados.length - 2].data)} o cálculo saiu por{" "}
      {rotuloDoProtocolo(usados[usados.length - 2].protocolo)}; de{" "}
      {emBRCompleto(virada.data)} em diante, por {rotuloDoProtocolo(virada.protocolo)}, porque o
      atleta passou dos 18 anos e o protocolo de criança deixa de valer. Parte da
      diferença entre esses dois pontos é a troca de equação, e não mudança do atleta.
      As outras medidas do gráfico não têm esse efeito: são medidas, não estimativas.
    </div>
  );
}
